<template>
  <div class="flex flex-col gap-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">指南凝练</h1>
        <p class="text-sm text-slate-500 mt-1">辅助科研管理人员优化项目指南，确保指南方向的先进性、指标的合理性</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">第 1 环节</span>
        <span class="px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-full text-xs font-semibold">已完成</span>
      </div>
    </div>

    <!-- Main Content Card -->
    <div class="glass-card rounded-3xl p-6">
      <!-- Section 1: Analysis Dimensions -->
      <div class="mb-8">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">分析维度配置</h2>
        </div>
        <p class="text-sm text-slate-500 mb-4 ml-10">选择需要分析的维度，系统将基于所选维度对指南进行智能凝练分析</p>

        <div class="grid grid-cols-2 gap-4 ml-10">
          <label class="relative cursor-pointer" @click="toggleDimension('direction')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full"
                 :class="selectedDimensions.includes('direction') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors"
                     :class="selectedDimensions.includes('direction') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors"
                        :class="selectedDimensions.includes('direction') ? 'text-blue-700' : 'text-slate-700'">指南方向优化</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors"
                         :class="selectedDimensions.includes('direction') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('direction')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">与领域前沿技术方向对比，判断指南方向是否具备先进性，评估是否弥补技术空白</p>
                </div>
              </div>
            </div>
          </label>

          <label class="relative cursor-pointer" @click="toggleDimension('indicator')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full"
                 :class="selectedDimensions.includes('indicator') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors"
                     :class="selectedDimensions.includes('indicator') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors"
                        :class="selectedDimensions.includes('indicator') ? 'text-blue-700' : 'text-slate-700'">指南指标优化</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors"
                         :class="selectedDimensions.includes('indicator') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('indicator')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">分析关键研究内容是否设计相关指标，辅助指标构建与量化，确保指标可考核</p>
                </div>
              </div>
            </div>
          </label>

          <label class="relative cursor-pointer" @click="toggleDimension('duplicate')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full"
                 :class="selectedDimensions.includes('duplicate') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors"
                     :class="selectedDimensions.includes('duplicate') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors"
                        :class="selectedDimensions.includes('duplicate') ? 'text-blue-700' : 'text-slate-700'">指南内容查重</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors"
                         :class="selectedDimensions.includes('duplicate') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('duplicate')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">与已有指南进行查重对比，研判是否存在已立项的指南，减少重复立项风险</p>
                </div>
              </div>
            </div>
          </label>

          <label class="relative cursor-pointer" @click="toggleDimension('lowIndicator')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full"
                 :class="selectedDimensions.includes('lowIndicator') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors"
                     :class="selectedDimensions.includes('lowIndicator') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors"
                        :class="selectedDimensions.includes('lowIndicator') ? 'text-blue-700' : 'text-slate-700'">指标偏低分析</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors"
                         :class="selectedDimensions.includes('lowIndicator') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('lowIndicator')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">与顶层实施方案对比，与外部科技指标对比，分析指标是否降低/偏低</p>
                </div>
              </div>
            </div>
          </label>
        </div>
      </div>

      <!-- Section 2: Guide Input -->
      <div class="mb-8">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">指南内容输入</h2>
        </div>
        <p class="text-sm text-slate-500 mb-4 ml-10">输入需要凝练分析的指南内容，支持文本粘贴或文件上传</p>

        <div class="ml-10">
          <div class="relative">
            <textarea
              v-model="guideContent"
              class="premium-input w-full h-48 rounded-2xl p-5 text-sm leading-relaxed resize-none"
              placeholder="请粘贴指南内容，或点击下方上传文件..."
            ></textarea>
            <div class="absolute bottom-4 right-4 text-xs text-slate-400">
              {{ guideContent.length }} 字符
            </div>
          </div>

          <!-- File Upload -->
          <div class="mt-4 flex items-center gap-4">
            <button class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium text-slate-600 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
              </svg>
              上传文件
            </button>
            <span class="text-xs text-slate-400">支持 PDF、DOCX、TXT 格式，最大 10MB</span>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <div class="flex justify-center">
        <button class="btn-action px-10 py-4 rounded-2xl text-white font-bold text-base flex items-center gap-3">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>
          启动智能凝练分析
        </button>
      </div>
    </div>

    <!-- Analysis History -->
    <div class="glass-card rounded-3xl p-6">
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">分析历史</h2>
        </div>
        <button class="text-sm text-blue-600 hover:text-blue-700 font-medium">查看全部</button>
      </div>

      <div class="space-y-3">
        <div v-for="(item, index) in historyItems" :key="index" class="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer">
          <div class="flex items-center gap-4">
            <div class="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
              <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <div>
              <h4 class="font-semibold text-slate-700 text-sm">{{ item.title }}</h4>
              <p class="text-xs text-slate-500 mt-0.5">{{ item.dimensions }} · {{ item.time }}</p>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <span class="px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-medium">已完成</span>
            <button class="p-2 hover:bg-white rounded-lg transition-colors">
              <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GuideRefine',
  data() {
    return {
      selectedDimensions: ['direction', 'indicator'],
      guideContent: '',
      historyItems: [
        { title: '2025年度人工智能专项指南凝练', dimensions: '4个维度', time: '2小时前' },
        { title: '生物医药创新项目指南优化', dimensions: '3个维度', time: '昨天' },
        { title: '新材料领域重点研发指南分析', dimensions: '4个维度', time: '3天前' }
      ]
    }
  },
  methods: {
    toggleDimension(dimension) {
      const index = this.selectedDimensions.indexOf(dimension)
      if (index > -1) {
        this.selectedDimensions.splice(index, 1)
      } else {
        this.selectedDimensions.push(dimension)
      }
    }
  }
}
</script>
